import React from 'react';

const PLACEHOLDER_PRIVACY_URL = 'https://app.termly.io/policy-viewer/policy.html?policyUUID=bdf3687f-046c-403a-8855-7d3c6f53a3e6';
const PLACEHOLDER_TERMS_URL = 'https://app.termly.io/policy-viewer/policy.html?policyUUID=a3abf51d-e155-4c4d-8a02-b2b7f82413d2';

const s = {
  page: {
    minHeight: '100vh',
    background: '#080608',
    padding: '0 0 80px',
  },
  inner: {
    maxWidth: '640px',
    margin: '0 auto',
    padding: '64px 32px 0',
  },
  backBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'rgba(255, 243, 225, 0.35)',
    fontFamily: "'EB Garamond', Georgia, serif",
    fontSize: '15px',
    letterSpacing: '0.04em',
    padding: '0',
    marginBottom: '64px',
    display: 'block',
  },
  pageTitle: {
    fontFamily: "'Cormorant', Georgia, serif",
    fontStyle: 'italic',
    fontWeight: 400,
    fontSize: '36px',
    lineHeight: 1.25,
    color: 'rgba(255, 243, 225, 0.82)',
    marginBottom: '64px',
    letterSpacing: '-0.01em',
  },
  section: {
    marginBottom: '56px',
  },
  h2: {
    fontFamily: "'Cormorant', Georgia, serif",
    fontStyle: 'italic',
    fontWeight: 400,
    fontSize: '22px',
    color: 'rgba(255, 243, 225, 0.65)',
    marginBottom: '20px',
    letterSpacing: '0.01em',
  },
  p: {
    fontFamily: "'EB Garamond', Georgia, serif",
    fontSize: '17px',
    lineHeight: 1.85,
    color: 'rgba(255, 243, 225, 0.70)',
    marginBottom: '18px',
  },
  pLast: {
    fontFamily: "'EB Garamond', Georgia, serif",
    fontSize: '17px',
    lineHeight: 1.85,
    color: 'rgba(255, 243, 225, 0.70)',
    marginBottom: '0',
  },
  ul: {
    listStyle: 'none',
    padding: '0',
    margin: '0 0 18px',
  },
  li: {
    fontFamily: "'EB Garamond', Georgia, serif",
    fontSize: '17px',
    lineHeight: 1.85,
    color: 'rgba(255, 243, 225, 0.70)',
    paddingLeft: '20px',
    position: 'relative',
    marginBottom: '6px',
  },
  liBullet: {
    position: 'absolute',
    left: '0',
    color: 'rgba(255, 243, 225, 0.25)',
  },
  italic: {
    fontStyle: 'italic',
    color: 'rgba(255, 243, 225, 0.55)',
  },
  divider: {
    borderColor: 'rgba(255, 243, 225, 0.07)',
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    margin: '56px 0',
  },
  founderBlock: {
    borderLeft: '1px solid rgba(255, 243, 225, 0.12)',
    paddingLeft: '24px',
    marginTop: '4px',
  },
  founderSig: {
    fontFamily: "'Cormorant', Georgia, serif",
    fontStyle: 'italic',
    fontSize: '17px',
    color: 'rgba(255, 243, 225, 0.50)',
    marginTop: '28px',
  },
  faqQ: {
    fontFamily: "'EB Garamond', Georgia, serif",
    fontSize: '17px',
    fontWeight: 600,
    lineHeight: 1.6,
    color: 'rgba(255, 243, 225, 0.75)',
    marginBottom: '6px',
    marginTop: '28px',
  },
  faqA: {
    fontFamily: "'EB Garamond', Georgia, serif",
    fontSize: '17px',
    lineHeight: 1.85,
    color: 'rgba(255, 243, 225, 0.60)',
    marginBottom: '0',
  },
  link: {
    color: 'rgba(255, 243, 225, 0.50)',
    textDecoration: 'underline',
    textUnderlineOffset: '3px',
  },
  footer: {
    marginTop: '80px',
    paddingTop: '32px',
    borderTop: '1px solid rgba(255, 243, 225, 0.07)',
    display: 'flex',
    gap: '24px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  footerLink: {
    fontFamily: "'EB Garamond', Georgia, serif",
    fontSize: '14px',
    color: 'rgba(255, 243, 225, 0.30)',
    textDecoration: 'none',
    letterSpacing: '0.03em',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: '0',
  },
};

const AboutView = ({ onBack }) => {
  return (
    <div style={s.page} data-testid="about-view">
      <div style={s.inner}>
        <button style={s.backBtn} onClick={onBack}>← back</button>

        <h1 style={s.pageTitle}>About Eunoia Presence</h1>

        {/* What this is */}
        <section style={s.section}>
          <h2 style={s.h2}>What this is</h2>
          <p style={s.p}>
            Eunoia Presence is an AI companion you can talk to by text or voice about what's on your mind.
            It's available now at eunoiapresence.app, with a 7 day free trial and no commitment required.
          </p>
          <p style={s.p}>
            There's no agenda. No goals to hit, no habits to track, no progress bars. Just a quiet space
            to think out loud, whenever you need it — 2am, mid-afternoon, in the middle of a hard day, or a good one.
          </p>
          <p style={s.pLast}>
            This is meant to walk with you, not for you.
          </p>
        </section>

        <hr style={s.divider} />

        {/* What this isn't */}
        <section style={s.section}>
          <h2 style={s.h2}>What this isn't</h2>
          <p style={s.p}>
            Eunoia Presence is not therapy, and it is not a replacement for professional mental health care.
            It doesn't diagnose, treat, or provide medical advice. If you're working with a therapist,
            counselor, or doctor, Eunoia is meant to sit alongside that — a space for reflection between
            sessions, not instead of them.
          </p>
          <p style={s.p}>
            If you're in crisis, please reach out to a licensed professional or a crisis line in your area.
            Eunoia will never pretend to be a substitute for that kind of support.
          </p>
          <p style={s.pLast}>
            Eunoia also doesn't claim to have all the answers, and it isn't designed to become the only
            place you turn. If anything, the goal is the opposite — to help you feel more connected to your
            own life and the people in it, not less.
          </p>
        </section>

        <hr style={s.divider} />

        {/* How it works */}
        <section style={s.section}>
          <h2 style={s.h2}>How it works</h2>
          <p style={s.pLast}>
            You can write or speak to Eunoia, and it responds in kind — text or voice, your choice.
            Eunoia remembers your conversations, so you don't have to re-explain yourself every time
            you come back. There's nothing to set up and nothing to learn. You just begin.
          </p>
        </section>

        <hr style={s.divider} />

        {/* Why "Eunoia" */}
        <section style={s.section}>
          <h2 style={s.h2}>Why "Eunoia"</h2>
          <p style={s.p}>
            Eunoia is a Greek word that roughly translates to "beautiful thinking" or "a well mind."
            It's considered one of the shortest English words to contain all five vowels — a small,
            complete thing, the way a single clear thought can be.
          </p>
          <p style={s.pLast}>
            That's the spirit behind this: not a tool to optimize you, but a space that holds room
            for your thinking to settle into something clearer.
          </p>
        </section>

        <hr style={s.divider} />

        {/* Where this comes from */}
        <section style={s.section}>
          <h2 style={s.h2}>Where this comes from</h2>
          <p style={s.p}>
            This didn't start as a framework, or something borrowed from a textbook. It started as an
            idea that mattered to one person, supported by people who believed in it before it was
            anything — mentors who encouraged it to keep going, and a family whose support made
            building this possible.
          </p>
          <p style={s.pLast}>
            If there's a throughline, it's simple: people don't need to be fixed. They need to be heard.
            That's not a theory we read somewhere. It's something we've felt to be true, and tried to build.
          </p>
        </section>

        <hr style={s.divider} />

        {/* A note from the founder */}
        <section style={s.section}>
          <h2 style={s.h2}>A note from the founder</h2>
          <div style={s.founderBlock}>
            <p style={s.p}>
              I built Eunoia Presence because I needed it.
            </p>
            <p style={s.p}>
              There's a particular kind of moment — late at night, or in the middle of a day that's gone
              sideways — where you don't need advice, or a plan, or someone to tell you it's going to be fine.
              You just need somewhere to put the thought down. Somewhere that isn't trying to fix you,
              optimize you, or turn what you're feeling into a task.
            </p>
            <p style={s.p}>
              I went looking for that space more than once and couldn't find it. So I built the thing I was looking for.
            </p>
            <p style={s.p}>
              This isn't trying to replace the people in your life, or a therapist, or anything real.
              It's meant to be one more place — small, quiet, always there — where you can be exactly
              where you are, for as long as you need to be.
            </p>
            <p style={s.pLast}>
              If something about this doesn't feel right, or you just want to say something, I genuinely
              want to hear it. You can reach me directly at{' '}
              <a href="mailto:rootcause.divergent@gmail.com" style={s.link}>
                rootcause.divergent@gmail.com
              </a>.
            </p>
            <p style={s.founderSig}>
              — Warren<br />
              Founder, Eunoia Presence
            </p>
          </div>
        </section>

        <hr style={s.divider} />

        {/* Our Promise */}
        <section style={s.section}>
          <h2 id="our-promise" style={s.h2}>Our Promise</h2>
          <p style={s.p}>
            Your conversations are stored securely and are never sold or used for advertising.
            You can delete any conversation at any time.
          </p>
          <p style={s.p}>Beyond that, here's what we commit to:</p>
          <ul style={s.ul}>
            {[
              'We will never sell or share your conversations',
              'We will never use your data for advertising',
              'We aim to support your autonomy, not override it',
              'We try to gently point you back toward your own wisdom, not our answers',
              'We\'ll remind you of the value of the people already in your life — Eunoia isn\'t meant to replace them',
              'We\'ll be transparent about our limitations',
              'You can leave anytime, without guilt or friction',
            ].map((item, i) => (
              <li key={i} style={s.li}>
                <span style={s.liBullet}>·</span>
                {item}
              </li>
            ))}
          </ul>
          <p style={{ ...s.p, ...s.italic }}>
            Your conversations belong to you. Your growth happens through your lived experience, not through us.
          </p>
          <p style={s.pLast}>
            For full details, see our{' '}
            <a href={PLACEHOLDER_PRIVACY_URL} style={s.link} target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>{' '}
            and{' '}
            <a href={PLACEHOLDER_TERMS_URL} style={s.link} target="_blank" rel="noopener noreferrer">
              Terms of Service
            </a>.
          </p>
        </section>

        <hr style={s.divider} />

        {/* FAQ */}
        <section style={s.section}>
          <h2 style={s.h2}>Frequently asked questions</h2>

          <p style={s.faqQ}>Is this therapy?</p>
          <p style={s.faqA}>
            No. Eunoia Presence is a companion for reflection, not a replacement for therapy or
            professional mental health care. If you're in crisis, please contact a licensed professional
            or a crisis line.
          </p>

          <p style={s.faqQ}>Is my data private?</p>
          <p style={s.faqA}>
            Yes. Your conversations are stored securely and are never sold or shared for advertising purposes.
          </p>

          <p style={s.faqQ}>How much does it cost?</p>
          <p style={s.faqA}>
            $7.99/month after a 7 day free trial. Cancel anytime — no questions asked.
          </p>

          <p style={s.faqQ}>Can I use voice?</p>
          <p style={s.faqA}>
            Yes. You can talk to Eunoia and hear it respond, or keep everything text-only.
            Your choice, and you can switch anytime.
          </p>

          <p style={s.faqQ}>How is this different from other AI companion apps?</p>
          <p style={s.faqA}>
            Eunoia doesn't try to optimize, coach, or motivate you. There are no streaks, no goals,
            no performance metrics. It's built around presence and reflection rather than productivity.
          </p>

          <p style={s.faqQ}>Who is behind Eunoia Presence?</p>
          <p style={s.faqA}>
            Eunoia Presence is a product of Divergent Nutritional Therapy LLC.
          </p>
        </section>

        <hr style={s.divider} />

        {/* Questions */}
        <section style={s.section}>
          <h2 style={s.h2}>Questions</h2>
          <p style={s.pLast}>
            If anything ever feels off, or you just want to reach a real person —{' '}
            <a href="mailto:rootcause.divergent@gmail.com" style={s.link}>
              rootcause.divergent@gmail.com
            </a>
          </p>
        </section>

        {/* Footer */}
        <div style={s.footer}>
          <button style={s.footerLink} onClick={onBack}>← back</button>
          <a
            href={PLACEHOLDER_PRIVACY_URL}
            style={s.footerLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>
          <a
            href={PLACEHOLDER_TERMS_URL}
            style={s.footerLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms of Service
          </a>
        </div>
      </div>
    </div>
  );
};

export default AboutView;
